/**
 * RAG Indexer Frontend Component
 * 
 * Orchestrates RAG indexing in 3 phases:
 * Phase 1 (Identification): Worker identifies articles needing processing
 * Phase 2 (Preparation): Frontend renders templates + Worker stores prepared data
 * Phase 3 (Indexing): Worker chunks content and generates embeddings
 * 
 * Key architecture:
 * - Phase 1: Worker determines which articles to process
 * - Phase 2: Frontend orchestrates (fetches, renders locally via Handlebars)
 * - Phase 3: Worker processes prepared articles (chunking + embeddings)
 * 
 * Frontend responsibilities:
 * - Orchestrates the workflow
 * - Renders Handlebars templates (no CSP issues)
 * - Displays progress and logs
 */

import React, { useState, useCallback } from 'react';
import { routedFetch } from '../utils/api-router';
import { useTranslation } from '../context/TranslationContext';
import { runTemplate } from '../wa-tools/handlebarIA-services';
import { getCompiledTemplates } from '../utils/config-calls';
import debug from '@commons-shared/debug';

/**
 * Chunk article content by sections (DEPRECATED - moved to worker)
 * Kept for backward compatibility, but not used in Architecture #2
 */
function chunkContent(content, maxChunkSize = 1000) {
  const chunks = [];
  
  // Split by sections/paragraphs first
  const sections = content.split(/\n\n+/);
  
  let currentChunk = '';
  for (const section of sections) {
    if ((currentChunk + section).length > maxChunkSize && currentChunk) {
      chunks.push({
        content: currentChunk.trim(),
        order: chunks.length,
      });
      currentChunk = section;
    } else {
      currentChunk += (currentChunk ? '\n\n' : '') + section;
    }
  }
  
  if (currentChunk.trim()) {
    chunks.push({
      content: currentChunk.trim(),
      order: chunks.length,
    });
  }
  
  return chunks;
}

/**
 * Load Handlebars templates for article rendering
 * Uses handlebarIA-services.runTemplate() for consistent template handling
 * 
 * @param {string} iaLanguage - Language code (e.g., 'FR')
 * @returns {Promise<Object>} Compiled templates
 */
async function loadHandlebarsTemplates(iaLanguage = 'FR') {
  try {
    // Load templates via config API
    // This loads all templates of type 'article_rag_action' for the given language
    const response = await getCompiledTemplates(iaLanguage, 'article_rag_action');
    
    if (!response.success || !response.templates) {
      throw new Error('Invalid response format from templates API');
    }
    
    // Convert array to object indexed by source_key for easier access
    const templatesMap = {};
    response.templates.forEach(template => {
      templatesMap[template.source_key] = template;
    });
    
    return templatesMap;
  } catch (error) {
    console.error('[RAG Frontend] Error loading templates:', error);
    throw new Error(`Failed to load Handlebars templates: ${error.message}`);
  }
}

/**
 * Main RAG Indexer Component
 */
export function RAGIndexer({ worldId }) {
  const { t } = useTranslation();
  const [indexing, setIndexing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [logMessages, setLogMessages] = useState([]);
  const [indexingState, setIndexingState] = useState(null);
  const [currentUsername, setCurrentUsername] = useState(null);
  
  // Computed values for button availability based on stats
  const canPrepare = stats?.prep?.pending > 0 || stats?.prep?.failed > 0;
  const canIndex = stats?.index?.pending > 0 || stats?.index?.failed > 0;

  const addLog = useCallback((message) => {
    setLogMessages((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  }, []);

  // Load current username from localStorage
  React.useEffect(() => {
    const username = localStorage.getItem('wa_username');
    setCurrentUsername(username);
  }, []);

  // Check indexing status on component mount or world change
  const checkIndexingStatus = useCallback(async () => {
    try {
      const response = await routedFetch(`/api/rag/check-status?worldId=${worldId}`, {}, true);
      if (response.success && response.data) {
        const { indexing_in_progress, username } = response.data;
        const canResume = indexing_in_progress && username === currentUsername;
        setIndexingState({
          in_progress: indexing_in_progress,
          username,
          can_resume: canResume
        });
      }
    } catch (error) {
      debug.error('[RAG Frontend] Error checking indexing status:', error);
    }
  }, [worldId, currentUsername]);

  // Load indexing status when component mounts or world changes
  React.useEffect(() => {
    if (currentUsername && worldId) {
      checkIndexingStatus();
    }
  }, [worldId, currentUsername, checkIndexingStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await routedFetch(`/api/rag/status?worldId=${worldId}`, {}, true);
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('[RAG Frontend] Error fetching stats:', error);
    }
  }, [worldId]);

  // Load stats when component mounts or world changes
  React.useEffect(() => {
    if (worldId) {
      fetchStats();
    }
  }, [worldId, fetchStats]);

  const startPhase1 = useCallback(async () => {
    setIndexing(true);
    setError(null);
    setLogMessages([]);
    
    try {
      // Charger username depuis localStorage (plus fiable que l'état React)
      const username = localStorage.getItem('wa_username');
      if (!username) {
        const errorMsg = 'Username not found. Please log in to WorldAnvil first.';
        addLog(`✗ ${errorMsg}`);
        throw new Error(errorMsg);
      }
      
      const routingConfig = JSON.parse(localStorage.getItem('routing_config') || '{}');
      const iaLanguage = routingConfig?.text?.['X-Working-Language'];
      
      if (!iaLanguage) {
        const errorMsg = 'IA working language not configured. Please set X-Working-Language in routing_config.';
        addLog(`✗ ${errorMsg}`);
        throw new Error(errorMsg);
      }
      
      // Lock step 'identification'
      addLog('Locking identification step...');
      const lockResponse = await routedFetch(
        `/api/rag/lock-step?worldId=${worldId}&step=identification&username=${username}`,
        { method: 'POST' }
      );
      if (!lockResponse.success) {
        throw new Error(`Failed to lock step: ${lockResponse.error}`);
      }
      addLog('✓ Identification step locked');
      
      addLog('Phase 1: Identifying articles to process (batch processing)...');
      
      // Process articles in batches of 10
      const batchSize = 10;
      let offset = 0;
      let hasMore = true;
      let totalPending = 0;
      let totalSkipped = 0;
      let totalFetched = 0;
      let totalArticles = 0;
      let totalSecrets = 0;
      const cumulativeReasons = {
        draft: 0,
        noRagTag: 0,
        invalidEntityClass: 0,
        alreadyUpToDate: 0,
      };

      while (hasMore) {
        addLog(`Processing batch at offset ${offset}...`);
        
        const initData = await routedFetch(
          `/api/rag/init-indexing?worldId=${worldId}&iaLanguage=${iaLanguage}&username=${username}&offset=${offset}&batchSize=${batchSize}`,
          {
            method: 'POST',
          }
        );
      
        if (!initData.success) {
          throw new Error(initData.error || 'Initialization failed');
        }

        const { batchTotal, batchArticles: batchArt, batchSecrets: batchSec, batchPending, batchSkipped, reasons, hasMore: more } = initData.data;
        
        totalFetched += batchTotal;
        totalArticles += batchArt || 0;
        totalSecrets += batchSec || 0;
        totalPending += batchPending;
        totalSkipped += batchSkipped;
        
        // Accumulate reasons
        Object.keys(reasons).forEach(key => {
          cumulativeReasons[key] = (cumulativeReasons[key] || 0) + (reasons[key] || 0);
        });
        
        addLog(`  ✓ Batch complete: ${batchPending} pending, ${batchSkipped} skipped`);
        
        hasMore = more;
        offset += batchSize;
        
        // Pause between batches to avoid overwhelming the system
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      addLog(`✓ Phase 1 complete: Processed ${totalFetched} items (${totalArticles} articles, ${totalSecrets} secrets)`);
      addLog(`  → ${totalPending} pending items ready for Phase 2`);
      if (totalSkipped > 0) {
        addLog(`  - ${totalSkipped} skipped:`);
        if (cumulativeReasons?.draft > 0) addLog(`      • ${cumulativeReasons.draft} drafts`);
        if (cumulativeReasons?.noRagTag > 0) addLog(`      • ${cumulativeReasons.noRagTag} with NoRag tag`);
        if (cumulativeReasons?.invalidEntityClass > 0) addLog(`      • ${cumulativeReasons.invalidEntityClass} without RAG template`);
        if (cumulativeReasons?.alreadyUpToDate > 0) addLog(`      • ${cumulativeReasons.alreadyUpToDate} already up-to-date`);
      }

      // Unlock step
      addLog('Unlocking identification step...');
      const unlockResponse = await routedFetch(
        `/api/rag/unlock-step?worldId=${worldId}`,
        { method: 'POST' }
      );
      if (!unlockResponse.success) {
        addLog(`⚠️ Warning: Failed to unlock step: ${unlockResponse.error}`);
      } else {
        addLog('✓ Identification step unlocked');
      }

      await fetchStats();
    } catch (error) {
      addLog(`✗ Phase 1 error: ${error.message}`);
      setError(error.message);
    } finally {
      setIndexing(false);
    }
  }, [worldId, currentUsername, addLog, fetchStats]);

  const startPhase2 = useCallback(async () => {
    setIndexing(true);
    setError(null);
    setLogMessages([]);
    
    try {
      // Charger username depuis localStorage (plus fiable que l'état React)
      const username = localStorage.getItem('wa_username');
      if (!username) {
        throw new Error('Username not found. Please log in to WorldAnvil first.');
      }
      
      const routingConfig = JSON.parse(localStorage.getItem('routing_config') || '{}');
      const iaLanguage = routingConfig?.text?.['X-Working-Language'];
      
      if (!iaLanguage) {
        throw new Error('IA working language not configured');
      }
      
      // Lock step 'preparation'
      addLog('Locking preparation step...');
      const lockResponse = await routedFetch(
        `/api/rag/lock-step?worldId=${worldId}&step=preparation&username=${username}`,
        { method: 'POST' }
      );
      if (!lockResponse.success) {
        throw new Error(`Failed to lock step: ${lockResponse.error}`);
      }
      addLog('✓ Preparation step locked');
      
      addLog('Phase 2: Preparing articles (rendering templates)...');
      
      // Load templates
      addLog('Loading Handlebars templates...');
      const templates = await loadHandlebarsTemplates(iaLanguage);
      addLog(`Loaded ${Object.keys(templates).length} templates`);

      // Fetch and process articles in batches
      // Note: always fetch at offset=0 because processed/failed articles change status
      // and disappear from the pending query results between batches
      let processedCount = 0;
      let failedCount = 0;
      const pageSize = 10;
      const maxIterations = 500; // Safety guard against infinite loops
      let iteration = 0;

      while (iteration < maxIterations) {
        iteration++;
        addLog(`Fetching articles (batch #${iteration}, limit: ${pageSize})...`);
        
        const fetchResponse = await routedFetch(
          `/api/rag/fetch-articles?worldId=${worldId}&limit=${pageSize}&offset=0`,
          {},
          true
        );

        if (!fetchResponse.success) {
          throw new Error(fetchResponse.error || 'Fetch failed');
        }

        const { articles, failedCount: batchFailedCount, failedArticles, hasMore } = fetchResponse.data;
        
        // Log les erreurs s'il y en a
        if (batchFailedCount > 0) {
          addLog(`✗ ERROR: ${batchFailedCount} article(s) failed to fetch from WorldAnvil`);
          if (failedArticles && failedArticles.length > 0) {
            failedArticles.slice(0, 3).forEach(id => {
              addLog(`    ✗ Failed article ID: ${id}`);
            });
            if (failedArticles.length > 3) {
              addLog(`    ✗ ... and ${failedArticles.length - 3} more failed articles`);
            }
          }
          failedCount += batchFailedCount;
        }
        
        if (articles.length === 0) {
          addLog('✓ All articles processed');
          break;
        }

        addLog(`Fetched ${articles.length} articles`);

        // Process each article with template rendering
        const processedArticles = [];
        const articlesWithErrors = [];

        for (let idx = 0; idx < articles.length; idx++) {
          const article = articles[idx];

          try {
            addLog(`Processing article ${idx + 1}/${articles.length}: ${article.title}`);

            // Render templates
            addLog(`  → Rendering Handlebars templates...`);
            const contentParts = [];

            // Determine template to use
            const templateName = (article.customArticleTemplate?.title && article.customArticleTemplate.title.trim()) 
              ? article.customArticleTemplate.title 
              : article.entityClass;

            // Render title
            try {
              const titleResult = await runTemplate(null, { article: article }, templateName + '@to_title', iaLanguage,'text');
              if (titleResult?.text) {
                contentParts.push({ key: 'titleRendered', value: titleResult.text });
              }
            } catch (e) {
              addLog(`  ⚠ Warning: Could not render title template`);
            }

            // Render context
            try {
              const contextResult = await runTemplate(null, { article: article }, templateName + '@to_context', iaLanguage,'text');
              if (contextResult?.text) {
                contentParts.push({ key: 'contextRendered', value: contextResult.text });
              }
            } catch (e) {
              addLog(`  ⚠ Warning: Could not render context template`);
            }

            // Render content
            try {
              const contentResult = await runTemplate(null, { article: article }, templateName + '@to_content', iaLanguage, 'markdown');
              if (contentResult?.text) {
                contentParts.push({ key: 'contentRendered', value: contentResult.text });
              }
            } catch (e) {
              addLog(`  ⚠ Warning: Could not render content template`);
            }

            // Render canon
            try {
              const canonResult = await runTemplate(null, { article: article }, templateName + '@to_canon', iaLanguage, 'markdown');
              if (canonResult?.text) {
                contentParts.push({ key: 'canonRendered', value: canonResult.text });
              }
            } catch (e) {
              addLog(`  ⚠ Warning: Could not render canon template`);
            }

            if (contentParts.length === 0) {
              const errorMsg = 'No rendered content available from templates';
              addLog(`  ⚠ ${errorMsg}`);
              failedCount++;
              
              // Ajouter l'article aux erreurs pour marquage dans le worker
              articlesWithErrors.push({
                articleId: article.id,
                worldId,
                error: errorMsg
              });
              
              continue;
            }

            // Convert WorldAnvil PHP date to UTC ISO string
            const convertWADateToUTC = (waDate) => {
              if (!waDate) return new Date().toISOString();
              const date = new Date(waDate);
              if (isNaN(date.getTime())) return new Date().toISOString();
              return date.toISOString();
            };

            // Build article for storage
            const articleForStorage = {
              articleId: article.id,
              worldId,
              url: article.url || '',
              ...Object.fromEntries(contentParts.map(p => [p.key, p.value])),
              articleMetadata: {
                title: article.title,
                excerpt: article.excerpt,
                entityClass: article.entityClass,
                customArticleTemplate: article.customArticleTemplate?.title,
                articleUpdatedAt: convertWADateToUTC(article.content?.updateDate?.date),
              },
            };

            processedArticles.push(articleForStorage);
            addLog(`  ✓ Templates rendered successfully`);

          } catch (articleError) {
            const errorMsg = articleError.message || 'Unknown error';
            const articleTitle = article?.title || article?.name || 'Unknown';
            addLog(`  ✗ Error processing ${articleTitle}: ${errorMsg}`);
            failedCount++;
            
            // Ajouter l'article aux erreurs pour marquage dans le worker
            articlesWithErrors.push({
              articleId: article.id,
              worldId,
              error: errorMsg
            });
          }

          setProgress(Math.min(95, (processedCount + failedCount + idx + 1) / (processedCount + failedCount + articles.length) * 100)); // Progress based on total processed
        }

        // Send processed articles to worker for preparation
        if (processedArticles.length > 0) {
          addLog(`Sending ${processedArticles.length} articles to worker for preparation...`);

          const storeData = await routedFetch('/api/rag/store-processed-articles', {
            method: 'POST',
            body: JSON.stringify({ articles: processedArticles }),
          });

          if (!storeData.success) {
            throw new Error(storeData.error || 'Storage failed');
          }

          addLog(`✓ Prepared: ${storeData.data.processed} articles`);
          processedCount += storeData.data.processed;

          if (storeData.data.failedArticles.length > 0) {
            addLog(`⚠ Failed: ${storeData.data.failedArticles.length} articles`);
            failedCount += storeData.data.failedArticles.length;
          }
        }
        
        // Send articles with errors to worker for marking as failed
        if (articlesWithErrors.length > 0) {
          addLog(`Marking ${articlesWithErrors.length} articles as failed...`);
          
          try {
            const markFailedData = await routedFetch('/api/rag/mark-articles-failed', {
              method: 'POST',
              body: JSON.stringify({ articles: articlesWithErrors }),
            });
            
            if (markFailedData.success) {
              addLog(`✓ Marked ${articlesWithErrors.length} articles as failed`);
            }
          } catch (markError) {
            addLog(`⚠ Warning: Could not mark failed articles: ${markError.message}`);
          }
        }

        // No need to increment offset: processed articles change status
        // and won't appear in the next query (offset always 0)
      }

      if (iteration >= maxIterations) {
        addLog(`⚠ Safety limit reached (${maxIterations} batches). Some articles may remain unprocessed.`);
      }

      addLog(`✓ Phase 2 complete!`);
      addLog(`  - Successfully prepared: ${processedCount} articles`);
      if (failedCount > 0) {
        addLog(`  - Failed: ${failedCount} articles`);
      }
      addLog(`  → Ready for Phase 3: RAG Indexing`);

      // Unlock step
      addLog('Unlocking preparation step...');
      const unlockResponse = await routedFetch(
        `/api/rag/unlock-step?worldId=${worldId}`,
        { method: 'POST' }
      );
      if (!unlockResponse.success) {
        addLog(`⚠️ Warning: Failed to unlock step: ${unlockResponse.error}`);
      } else {
        addLog('✓ Preparation step unlocked');
      }

      await fetchStats();
    } catch (error) {
      addLog(`✗ Phase 2 error: ${error.message}`);
      setError(error.message);
    } finally {
      setIndexing(false);
      setProgress(0);
    }
  }, [worldId, addLog, fetchStats]);

  const startPhase3 = useCallback(async () => {
    setIndexing(true);
    setError(null);
    setLogMessages([]);
    
    try {
      // Charger username depuis localStorage (plus fiable que l'état React)
      const username = localStorage.getItem('wa_username');
      if (!username) {
        throw new Error('Username not found. Please log in to WorldAnvil first.');
      }
      
      // Lock step 'indexation'
      addLog('Locking indexation step...');
      const lockResponse = await routedFetch(
        `/api/rag/lock-step?worldId=${worldId}&step=indexation&username=${username}`,
        { method: 'POST' }
      );
      if (!lockResponse.success) {
        throw new Error(`Failed to lock step: ${lockResponse.error}`);
      }
      addLog('✓ Indexation step locked');
      
      addLog('Phase 3: RAG Indexing (chunking + embeddings)...');
      addLog('Calling worker to process prepared articles...');
      
      const indexData = await routedFetch(`/api/rag/index-articles?worldId=${worldId}`, {}, true);
    
      if (!indexData.success) {
        throw new Error(indexData.error || 'Indexing failed');
      }

      const { processed, failed, totalChunks, totalEmbeddings } = indexData.data;
      addLog(`✓ Indexing complete!`);
      addLog(`  - Indexed: ${processed} articles`);
      addLog(`  - Failed: ${failed} articles`);
      addLog(`  - Created: ${totalChunks} chunks, ${totalEmbeddings} embeddings`);

      // Unlock step
      addLog('Unlocking indexation step...');
      const unlockResponse = await routedFetch(
        `/api/rag/unlock-step?worldId=${worldId}`,
        { method: 'POST' }
      );
      if (!unlockResponse.success) {
        addLog(`⚠️ Warning: Failed to unlock step: ${unlockResponse.error}`);
      } else {
        addLog('✓ Indexation step unlocked');
      }

      await fetchStats();
    } catch (error) {
      addLog(`✗ Phase 3 error: ${error.message}`);
      setError(error.message);
    } finally {
      setIndexing(false);
      setProgress(0);
    }
  }, [worldId, addLog, fetchStats]);

  return (
    <div className="rag-indexer">
      {/* Warning if indexing is in progress by another user */}
      {indexingState?.in_progress && !indexingState?.can_resume && (
        <div className="warning-message">
          <strong>⚠️ {t('RAG_ADMIN', 'INDEXING_IN_PROGRESS', 'RAG indexing in progress')}:</strong>
          {t('RAG_ADMIN', 'INDEXING_BY_OTHER_USER', 'Indexing is currently running by another user')} ({indexingState.username}).
          {t('RAG_ADMIN', 'WAIT_FOR_COMPLETION', 'Please wait for completion before starting a new indexing.')}
        </div>
      )}

      <div className="rag-controls">
        {/* Phase 1 Control - Toujours disponible */}
        <div className="control-group">
          <div className="phase-label">Phase 1: Identify</div>
          <button 
            onClick={() => startPhase1()} 
            disabled={indexing}
            className="btn btn-primary"
          >
            {t('RAG_ADMIN', 'IDENTIFY_ARTICLES', 'Identify Articles')}
          </button>
        </div>

        {/* Phase 2 Control - Disponible si prep_status pending ou failed */}
        <div className="control-group">
          <div className="phase-label">Phase 2: Prepare ({stats?.prep?.pending || 0} pending, {stats?.prep?.failed || 0} failed)</div>
          <button 
            onClick={() => startPhase2()} 
            disabled={indexing || !canPrepare}
            className="btn btn-primary"
          >
            {t('RAG_ADMIN', 'PREPARE_ARTICLES', 'Prepare Articles')}
          </button>
        </div>

        {/* Phase 3 Control - Disponible si index_status pending ou failed */}
        <div className="control-group">
          <div className="phase-label">Phase 3: Index ({stats?.index?.pending || 0} pending, {stats?.index?.failed || 0} failed)</div>
          <button 
            onClick={() => startPhase3()} 
            disabled={indexing || !canIndex}
            className="btn btn-primary"
          >
            {t('RAG_ADMIN', 'INDEX_ARTICLES', 'Index RAG')}
          </button>
        </div>

        {/* Refresh stats */}
        <div className="control-group">
          <div className="phase-label">&nbsp;</div>
          <button 
            onClick={fetchStats}
            disabled={indexing}
            className="btn btn-secondary"
          >
            {t('RAG_ADMIN', 'REFRESH_STATS', 'Refresh Stats')}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {indexing && (
        <div className="progress-container">
          <progress value={progress} max={100} className="progress-bar" />
          <span className="progress-text">{Math.round(progress)}%</span>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Log messages */}
      {logMessages.length > 0 && (
        <div className="log-viewer">
          <h3>Processing Log</h3>
          <pre className="log-content">
            {logMessages.join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
}

// CSS Styles
export const RAGIndexerStyles = `
.rag-indexer {
  padding: 20px;
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  background: var(--bg-secondary);
}

.rag-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.phase-label {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--color-primary);
  color: var(--text-inverse);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-hover);
}

.btn-secondary {
  background: var(--color-secondary);
  color: var(--text-inverse);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-secondary-hover);
}

.progress-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.progress-bar {
  flex: 1;
  height: 20px;
  border-radius: var(--radius-sm);
}

.progress-text {
  font-weight: 500;
  color: var(--text-primary);
}

.error-message {
  padding: 15px;
  background: var(--color-error-light);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-sm);
  color: var(--color-error);
  margin-bottom: 20px;
}

.rag-stats {
  padding: 15px;
  background: var(--bg-primary);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-sm);
  margin-bottom: 20px;
}

.rag-stats h3 {
  margin: 0 0 15px 0;
  color: var(--text-primary);
}

.stat-section {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--border-light);
}

.stat-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.stat-section h4 {
  margin: 0 0 10px 0;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

.rag-stats dl {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 10px;
}

.rag-stats dt {
  font-weight: 600;
  color: var(--text-muted);
}

.rag-stats dd {
  margin: 0;
  color: var(--text-primary);
}

.log-viewer {
  padding: 15px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-sm);
}

.log-viewer h3 {
  margin: 0 0 10px 0;
  color: var(--text-primary);
}

.log-content {
  margin: 0;
  padding: 10px;
  background: var(--bg-primary);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  max-height: 300px;
  overflow-y: auto;
  color: var(--text-primary);
}

.warning-message {
  padding: 15px;
  background: var(--color-warning-light);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius-sm);
  color: var(--color-warning);
  margin-bottom: 20px;
}

.warning-message strong {
  color: var(--color-warning);
}

.status-text {
  padding: 10px 15px;
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  align-self: center;
}

.btn-warning {
  background: var(--color-warning);
  color: var(--text-inverse);
}

.btn-warning:hover:not(:disabled) {
  background: var(--color-warning);
  filter: brightness(0.9);
  color: var(--text-inverse);
}
`;
